import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { DollarSign, Users, TrendingUp, Gift, Target, CheckCircle2, ArrowRight, Zap, Award, Star } from 'lucide-react';

const JoinOurTeam = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <DollarSign className="h-10 w-10" />,
      title: "Unlimited Earning Potential",
      description: "Earn up to ₹3000 per referral with our tier-based commission system. The more you refer, the more you earn!"
    },
    {
      icon: <Users className="h-10 w-10" />,
      title: "Build Your Network",
      description: "Create your own team of referrers and earn from their success. Multi-level commission structure."
    },
    {
      icon: <TrendingUp className="h-10 w-10" />,
      title: "Passive Income",
      description: "Earn recurring commissions from renewals. Your efforts today pay you for years to come."
    },
    {
      icon: <Gift className="h-10 w-10" />,
      title: "Exclusive Rewards",
      description: "Access to special bonuses, incentives, and recognition programs for top performers."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Sign Up for Free",
      description: "Create your account in minutes. No hidden fees, no upfront costs. Start immediately."
    },
    {
      step: "2",
      title: "Get Your Unique Referral Link",
      description: "Receive a personalized referral link and marketing materials to share with your network."
    },
    {
      step: "3",
      title: "Share & Promote",
      description: "Share your link on social media, WhatsApp, email, or directly with people you know."
    },
    {
      step: "4",
      title: "Earn Commissions",
      description: "Get paid for every successful referral. Track your earnings in real-time on your dashboard."
    }
  ];

  const commissionTiers = [
    {
      tier: "Bronze",
      sales: "0-10 sales",
      commission: "₹1500 per sale",
      color: "from-orange-400 to-orange-600"
    },
    {
      tier: "Silver",
      sales: "11-25 sales",
      commission: "₹2000 per sale",
      color: "from-gray-300 to-gray-500"
    },
    {
      tier: "Gold",
      sales: "26-50 sales",
      commission: "₹2500 per sale",
      color: "from-yellow-400 to-yellow-600"
    },
    {
      tier: "Platinum",
      sales: "51+ sales",
      commission: "₹3000 per sale",
      color: "from-purple-400 to-purple-600"
    }
  ];

  const features = [
    "Real-time earnings tracking dashboard",
    "Instant payment processing",
    "24/7 support from our team",
    "Free marketing materials and resources",
    "No hidden fees or charges",
    "Flexible payout options (Bank/UPI)"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-pink-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-600 via-yellow-500 to-pink-600">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NjIyMDY4NTZ8MA&ixlib=rb-4.1.0&q=85')"
            }}
          ></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Join Our Referral Team
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Turn your network into income! Earn up to ₹3000 per referral by helping others get lightning-fast internet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              className="h-16 px-10 text-xl font-bold bg-white text-red-600 hover:bg-gray-100 shadow-2xl"
            >
              <Award className="h-6 w-6 mr-2" />
              Start Earning Today
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="h-16 px-10 text-xl font-bold border-4 border-white text-white hover:bg-white/20 shadow-2xl"
            >
              Already a Member? Login
            </Button>
          </div>
        </div>
      </div>

      {/* Why Join Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Why Join Our Team?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Be part of India's fastest-growing ISP referral program with unmatched benefits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-4 border-red-200 hover:border-yellow-400 transition-all duration-300 shadow-lg hover:shadow-2xl bg-white">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}>
                    <div className="text-white">
                      {benefit.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              How It Works
            </h2>
            <p className="text-xl text-gray-700">Getting started is simple and takes less than 5 minutes</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 border-4 border-pink-200 hover:border-pink-400 transition-all shadow-lg h-full">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow-xl"
                      style={{ background: 'linear-gradient(135deg, #ec4899 0%, #dc2626 100%)' }}>
                      {item.step}
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-pink-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Commission Structure
            </h2>
            <p className="text-xl text-gray-700">Progress through tiers and increase your earnings</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commissionTiers.map((tier, index) => (
              <Card key={index} className="border-4 border-yellow-300 hover:scale-105 transition-all duration-300 shadow-xl">
                <CardContent className="p-6 text-center">
                  <div className={`w-full h-3 rounded-full mb-6 bg-gradient-to-r ${tier.color}`}></div>
                  <Star className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{tier.tier}</h3>
                  <p className="text-gray-600 mb-4">{tier.sales}</p>
                  <div className="bg-gradient-to-r from-red-50 to-yellow-50 rounded-xl p-4 border-2 border-yellow-300">
                    <p className="text-2xl font-bold text-red-600">{tier.commission}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYyMjQxOTkxfDA&ixlib=rb-4.1.0&q=85"
                alt="Team Success"
                className="rounded-2xl shadow-2xl border-4 border-yellow-300"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Everything You Need to Succeed
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                We provide all the tools, training, and support you need to build a successful referral business.
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-bold mb-6" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Real People, Real Success
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                Join thousands of successful referral partners earning consistent income every month.
              </p>
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border-4 border-red-300">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 text-yellow-500 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-lg text-gray-700 italic mb-4">
                  "I've been with Netix Fibernet referral program for 6 months and have already reached Gold tier. 
                  The support team is amazing and payments are always on time!"
                </p>
                <p className="font-bold text-gray-900">- Rajesh Kumar, Gold Partner</p>
                <p className="text-sm text-gray-600">Earning ₹50,000+ monthly</p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxlYXJuaW5nfGVufDB8fHx8MTc2MjI0MTk5NHww&ixlib=rb-4.1.0&q=85"
                alt="Earning Money"
                className="rounded-2xl shadow-2xl border-4 border-pink-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600 via-yellow-500 to-pink-600">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Ready to Start Earning?
          </h2>
          <p className="text-2xl text-white mb-10">
            Join over 1000+ active partners and start building your income today!
          </p>
          <Button
            onClick={() => navigate('/signup')}
            className="h-20 px-12 text-2xl font-bold bg-white text-red-600 hover:bg-gray-100 shadow-2xl hover:scale-105 transition-transform"
          >
            <Zap className="h-8 w-8 mr-3" />
            Create Your Free Account Now
          </Button>
          <p className="text-white mt-6 text-lg">No credit card required • Start earning in minutes</p>
        </div>
      </section>
    </div>
  );
};

export default JoinOurTeam;
